import React, { useState } from "react";

import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Button, IconButton, Stack, TextField, Typography } from "@mui/material";
import type { IApiServiceError } from "store";
import type { SourceRow } from "store/types/news";

import type { FeedConfigBuckets } from "../../context/NewsFeedContext";
import { floatingTextFieldSx, iconPillBtnSx, pillOutlinedBtnSx, sectionSubtitleSx, sectionTitleSx } from "./styles";

const normalizeSourceUrl = (raw: string): string => {
  const trimmed = raw.trim();
  if (!trimmed) return "";
  const withProtocol = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    const { hostname } = new URL(withProtocol);
    return hostname.replace(/^www\./i, "");
  } catch {
    return trimmed;
  }
};

type Props = {
  initialSources: FeedConfigBuckets<SourceRow>;
  onCreateSource: (payload: SourceRow) => Promise<SourceRow>;
  onUpdateSourcePriority: (source_url: string, priority: number, name: string) => Promise<SourceRow>;
  onDeleteSource: (source_url: string) => Promise<void>;
  onSelectSystemSource: (source_url: string, name: string) => Promise<void>;
  onUnselectSystemSource: (source_url: string) => Promise<void>;
};

export const SourcesPanel: React.FC<Props> = ({
  initialSources,
  onCreateSource,
  onUpdateSourcePriority,
  onDeleteSource,
  onSelectSystemSource,
  onUnselectSystemSource
}) => {
  const [sources, setSources] = useState<FeedConfigBuckets<SourceRow>>(initialSources);
  const [manualUrl, setManualUrl] = useState("");
  const [manualName, setManualName] = useState("");
  const [createSourceError, setCreateSourceError] = useState<string | null>(null);

  const moveSourcePriority = async (source_url: string, dir: number) => {
    const arr = [...sources.user];
    const i = arr.findIndex((s) => s.source_url === source_url);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= arr.length) return;
    const priorityA = j + 1;
    const priorityB = i + 1;
    const a = { ...arr[i], priority: priorityA };
    const b = { ...arr[j], priority: priorityB };
    arr[i] = b;
    arr[j] = a;
    setSources((curr) => ({ ...curr, user: arr }));
    await Promise.all([
      onUpdateSourcePriority(a.source_url, priorityA, a.name),
      onUpdateSourcePriority(b.source_url, priorityB, b.name)
    ]);
  };

  const removeSource = async (source_url: string) => {
    const target = sources.user.find((s) => s.source_url === source_url);
    if (!target) return;
    if (target.user_id == null) {
      await onUnselectSystemSource(source_url);
      setSources((curr) => ({
        system: [...curr.system, { ...target, priority: null }],
        user: curr.user.filter((s) => s.source_url !== source_url)
      }));
    } else {
      await onDeleteSource(source_url);
      setSources((curr) => ({
        ...curr,
        user: curr.user.filter((s) => s.source_url !== source_url)
      }));
    }
  };

  const addAvailableSource = async (source_url: string, name: string) => {
    const target = sources.system.find((s) => s.source_url === source_url);
    if (!target) return;
    await onSelectSystemSource(source_url, name);
    setSources((curr) => ({
      system: curr.system.filter((s) => s.source_url !== source_url),
      user: [...curr.user, { ...target, priority: curr.user.length + 1, name }]
    }));
  };

  const addManualSource = async () => {
    const url = normalizeSourceUrl(manualUrl);
    if (!url) return;
    const name = manualName.trim();
    setCreateSourceError(null);
    try {
      const created = await onCreateSource({
        user_id: null,
        name: name || url,
        source_url: url,
        priority: sources.user.length + 1
      });
      setManualUrl("");
      setManualName("");
      setSources((curr) => ({ ...curr, user: [...curr.user, created] }));
    } catch (error) {
      setCreateSourceError(Object.values((error as IApiServiceError).errors)[0][0] || "Something went wrong");
    }
  };

  return (
    <Stack gap="20px">
      <Stack gap="20px">
        <Stack>
          <Typography sx={sectionTitleSx}>Active Sources</Typography>
          <Typography sx={sectionSubtitleSx}>Sources we will pull articles from for your feed</Typography>
        </Stack>
        <Stack gap="10px">
          {sources.user.map((s, i) => (
            <Stack
              key={s.source_url}
              direction="row"
              alignItems="center"
              gap="2px"
              sx={{ pb: "10px", borderBottom: "1px solid #DBE8F5" }}
            >
              <Stack flex={1} minWidth={0} gap="2px">
                <Typography sx={{ fontSize: "15px", fontWeight: 700, lineHeight: "20px", color: "#071F35" }}>
                  {s.name}
                </Typography>
                <Typography sx={{ fontSize: "15px", fontWeight: 400, lineHeight: "20px", color: "#496682" }}>
                  {s.source_url}
                </Typography>
              </Stack>
              <Stack direction="row" gap="10px" alignItems="center" flexShrink={0}>
                <IconButton
                  disabled={i === sources.user.length - 1}
                  onClick={() => moveSourcePriority(s.source_url, 1)}
                  sx={iconPillBtnSx}
                >
                  <ExpandMoreIcon sx={{ fontSize: 20 }} />
                </IconButton>
                <IconButton disabled={i === 0} onClick={() => moveSourcePriority(s.source_url, -1)} sx={iconPillBtnSx}>
                  <ExpandLessIcon sx={{ fontSize: 20 }} />
                </IconButton>
                <IconButton
                  onClick={() => removeSource(s.source_url)}
                  sx={{ ...iconPillBtnSx, color: "#D32F2F", "&:hover": { bgcolor: "#FCE8E8" } }}
                >
                  <DeleteOutlineIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Stack>
            </Stack>
          ))}
        </Stack>
      </Stack>

      {sources.system.length ? (
        <Stack gap="20px">
          <Stack sx={{ pt: "20px" }}>
            <Typography sx={sectionTitleSx}>Available Sources</Typography>
            <Typography sx={sectionSubtitleSx}>Recommended sources you can add to your feed</Typography>
          </Stack>
          <Stack gap="10px">
            {sources.system.map((s) => (
              <Stack
                key={s.source_url}
                direction="row"
                alignItems="center"
                gap="2px"
                sx={{ pb: "10px", borderBottom: "1px solid #DBE8F5" }}
              >
                <Stack flex={1} minWidth={0} gap="2px">
                  <Typography sx={{ fontSize: "15px", fontWeight: 700, lineHeight: "20px", color: "#071F35" }}>
                    {s.name}
                  </Typography>
                  <Typography sx={{ fontSize: "15px", fontWeight: 400, lineHeight: "20px", color: "#496682" }}>
                    {s.source_url}
                  </Typography>
                </Stack>
                <Button onClick={() => addAvailableSource(s.source_url, s.name)} sx={pillOutlinedBtnSx}>
                  Add
                </Button>
              </Stack>
            ))}
          </Stack>
        </Stack>
      ) : null}

      <Stack gap="20px">
        <Stack sx={{ pt: "20px" }}>
          <Typography sx={sectionTitleSx}>Add Custom Source</Typography>
          <Typography sx={sectionSubtitleSx}>Provide a URL and optional display name to add your own source</Typography>
        </Stack>
        <Stack gap="10px" alignItems="flex-start">
          <TextField
            fullWidth
            label="URL"
            placeholder="http://example.com"
            value={manualUrl}
            onChange={(e) => {
              setCreateSourceError(null);
              setManualUrl(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") addManualSource();
            }}
            sx={floatingTextFieldSx}
          />
          <TextField
            fullWidth
            label="Display name"
            placeholder="Name"
            value={manualName}
            onChange={(e) => {
              setCreateSourceError(null);
              setManualName(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") addManualSource();
            }}
            sx={floatingTextFieldSx}
          />
          <Button onClick={addManualSource} sx={pillOutlinedBtnSx}>
            Add
          </Button>
          {createSourceError ? (
            <Typography component="p" role="alert" sx={{ fontSize: 12, color: "#D32F2F", lineHeight: 1.45, m: 0 }}>
              {createSourceError}
            </Typography>
          ) : null}
        </Stack>
      </Stack>
    </Stack>
  );
};
